import React, { useCallback, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withProps, withHandlers, compose } from 'recompose'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import AddressInput from '../AddressInput'
import Dropdown from '../../../Shared/Dropdown'
import PriceInput from '../../../Shared/Form/PriceInput'
import SearchBarFooterActions from '../../Shared/SearchBarFooterActions'
import SelectPricePeriod from '../../Shared/SelectPricePeriod/SelectPricePeriod'
import BedroomsBaths from '../BedroomsBaths'
import AdvancedFilter from '../AdvancedFilter'
import { priceLabelWithCutOff } from '../../../../lib/utils/money'
import { enhanceWithMap } from '../../../Shared/Form/TownSelect'

const enhance = compose(
  inject('store'),
  withProps(({ store }) => ({
    uiStateStore: store.uiStateStore,
    searchStateStore: store.searchStateStore,
    listingTypeStore: store.listingTypeStore,
    valueStateStore: store.valueStateStore
  })),
  enhanceWithMap,
  withHandlers(() => {
    const dropdownElements = {}

    const createCancelHandler = (name) => () => () => {
      dropdownElements[name]?.stateUpdaters?.deactivate?.()
    }

    return {
      registerDropdown: () => (name, el) => {
        if (el) {
          dropdownElements[name] = el
        }
      },
      cancelPriceDropdown: createCancelHandler('price'),
      cancelPricePeriodDropdown: createCancelHandler('pricePeriod'),
      cancelBedsBathsDropdown: createCancelHandler('bedsBaths'),
      cancelAdvancedFilterDropdown: createCancelHandler('advancedFilter'),
      onChangeTown: ({ valueStateStore }) => ({ value }) => {
        valueStateStore.setInputValue('townNames', value)
        valueStateStore.getSearchInfoDebounced()
      },
      onImmediateSubmit: ({ valueStateStore }) => () => {
        valueStateStore.fetchDataForListings()
        valueStateStore.getSearchInfoDebounced()
      },
      onChangePrice: ({ valueStateStore }) => (name, value) => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangePricePeriod: ({ valueStateStore }) => (values) => {
        valueStateStore.setInputValue('pricePeriods', values)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangeYear: ({ valueStateStore }) => (year) => {
        valueStateStore.setInputValue('year', year)
        valueStateStore.getSearchInfoDebounced()
      },
      onRadioButtonChange: ({ valueStateStore }) => (name) => (value) => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      }
    }
  }),
  observer
)

const SearchBarMobile = ({
  valueStateStore,
  listingTypeStore,
  uiStateStore,
  registerDropdown,
  cancelPriceDropdown,
  cancelPricePeriodDropdown,
  cancelBedsBathsDropdown,
  cancelAdvancedFilterDropdown,
  onChangeTown,
  onImmediateSubmit,
  onChangePrice,
  onChangePricePeriod,
  onChangeYear,
  onRadioButtonChange,
  openMapView
}) => {
  const [isListingTypeActive, setIsListingTypeActive] = useState(false)
  const [isPriceActive, setIsPriceActive] = useState(false)
  const [isPricePeriodActive, setIsPricePeriodActive] = useState(false)
  const [isBedsBathsActive, setIsBedsBathsActive] = useState(false)
  const [isAdvancedFilterActive, setIsAdvancedFilterActive] = useState(false)

  const handleSubmitTownChanges = useCallback(() => {
    valueStateStore.clearMapBounds()
    valueStateStore.removePolygon()
    onImmediateSubmit()
  }, [onImmediateSubmit, valueStateStore])

  const createApplyHandler = useCallback((cancelHandler) => () => {
    onImmediateSubmit()
    cancelHandler()
  }, [onImmediateSubmit])

  const handleApplyPrice = createApplyHandler(cancelPriceDropdown)
  const handleApplyPricePeriod = createApplyHandler(cancelPricePeriodDropdown)
  const handleApplyBedsBaths = createApplyHandler(cancelBedsBathsDropdown)
  const handleAdvancedFilterChange = createApplyHandler(cancelAdvancedFilterDropdown)

  return (
    <div className='SearchBar SearchBar--mobile'>
      <div className='SearchBar-mobileAddressInput'>
        <AddressInput
          value={valueStateStore.values.address || ''}
          onChange={(e) => valueStateStore.setInputValue('address', e.target.value)}
          placeholder='Address, neighbourhood, city, ZIP'
          options={toJS(valueStateStore.treeTownOptions)}
          optionsById={valueStateStore.optionsById}
          selectedOptionsWithParents={valueStateStore.selectedOptionsWithParents}
          selectedOptionsGroup={toJS(valueStateStore.selectedOptionsGroup)}
          onApplyResults={handleSubmitTownChanges}
          countResults={valueStateStore.countResults}
          onChangeTown={onChangeTown}
          enableAutocomplete={true}
          openMapView={openMapView}
        />
      </div>

      <div className='SearchBar-mobileFiltersContainer'>
        <div className='SearchBar-mobileFiltersRow'>
          <div className={`SearchBar-input SearchBar-input--listingType ${isListingTypeActive ? 'is-active' : ''}`}>
            <Dropdown
              id='listing-type-mobile'
              title={listingTypeStore.current === 'rentals' ? 'Rent' : 'Sale'}
              className='Dropdown--listingType Dropdown--searchBar Dropdown--mobile'
              onActiveChanged={setIsListingTypeActive}
            >
            <div className='ListingTypeSelector-options'>
              <div className='ListingTypeSelector-option'>
                <input
                  type='radio'
                  id='listing-type-rentals-mobile'
                  name='listing-type-mobile'
                  value='rentals'
                  checked={listingTypeStore.current === 'rentals'}
                  onChange={() => {
                    valueStateStore.saveSearchScope('rentals')
                    listingTypeStore.setCurrent('rentals')
                    valueStateStore.removePolygon()
                    onImmediateSubmit()
                  }}
                  className='ListingTypeSelector-radio'
                />
                <label htmlFor='listing-type-rentals-mobile' className='ListingTypeSelector-label'>
                  For Rent
                </label>
              </div>

              <div className='ListingTypeSelector-option'>
                <input
                  type='radio'
                  id='listing-type-sales-mobile'
                  name='listing-type-mobile'
                  value='sales'
                  checked={listingTypeStore.current === 'sales'}
                  onChange={() => {
                    valueStateStore.saveSearchScope('sales')
                    listingTypeStore.setCurrent('sales')
                    valueStateStore.removePolygon()
                    onImmediateSubmit()
                  }}
                  className='ListingTypeSelector-radio'
                />
                <label htmlFor='listing-type-sales-mobile' className='ListingTypeSelector-label'>
                  For Sale
                </label>
              </div>
            </div>
            </Dropdown>
          </div>

          <div className={`SearchBar-input SearchBar-input--price ${isPriceActive ? 'is-active' : ''}`}>
          <Dropdown
            id='price-mobile'
            className='Dropdown--searchBar'
            onActiveChanged={setIsPriceActive}
            ref={registerDropdown?.bind(null, 'price')}
            title={valueStateStore.priceTitle(uiStateStore.priceExtremum.cutOff)}
          >
            <div className='Dropdown-container'>
              <PriceInput
                priceExtremum={uiStateStore.priceExtremum}
                values={valueStateStore.priceValues}
                onChange={onChangePrice}
                onReset={valueStateStore.resetInputs}
                handleTemplate={priceLabelWithCutOff}
              />
            </div>
            <SearchBarFooterActions
              cancelHandler={cancelPriceDropdown}
              clearHandler={() => {
                valueStateStore.resetInputs(['priceFrom', 'priceTo'])
                valueStateStore.getSearchInfoDebounced()
              }}
              applyResultsHandler={handleApplyPrice}
              countResults={valueStateStore.countResults}
            />
          </Dropdown>
          </div>

          {listingTypeStore.current === 'rentals' &&
          <div className={`SearchBar-input SearchBar-input--pricePeriod ${isPricePeriodActive ? 'is-active' : ''}`}>
          <SelectPricePeriod
            id='price-period-mobile'
            pricePeriodYears={uiStateStore.pricePeriodYears}
            selectedYear={valueStateStore.values.year}
            pricePeriods={uiStateStore.pricePeriods}
            selectedPricePeriod={valueStateStore.values.pricePeriods || []}
            onChangePricePeriod={onChangePricePeriod}
            onChangeYear={onChangeYear}
            onResetInputToInitialValue={valueStateStore.resetInputToInitialValue}
            onActiveChanged={setIsPricePeriodActive}
            registerDropdown={registerDropdown}
            DropdownComponent={Dropdown}
            footer={
            <SearchBarFooterActions
              countResults={valueStateStore.countResults}
              cancelHandler={cancelPricePeriodDropdown}
              clearHandler={() => {
                valueStateStore.resetInputs(['pricePeriods', 'year'])
                valueStateStore.getSearchInfoDebounced()
              }}
              applyResultsHandler={handleApplyPricePeriod}
            />
            }
          />
          </div>
          }

          {(listingTypeStore.current === 'rentals' || listingTypeStore.current === 'sales') &&
          !valueStateStore.values.unitTypes?.includes('land') &&
          <div className={`SearchBar-input SearchBar-input--bedsBaths ${isBedsBathsActive ? 'is-active' : ''}`}>
          <Dropdown
            id='bedrooms-mobile'
            name='bedrooms'
            className='Dropdown--searchBar'
            title={valueStateStore.bedsBathsTitle}
            onActiveChanged={setIsBedsBathsActive}
            ref={registerDropdown?.bind(null, 'bedsBaths')}
          >
            <div className='Dropdown-container Dropdown-container--stacked'>
              <div className='Dropdown-itemColumn'>
                <BedroomsBaths
                  name='beds'
                  options={uiStateStore.bedsBathsOptions}
                  onChange={onRadioButtonChange('beds')}
                  value={valueStateStore.values.beds}
                  legend='Bedrooms'
                />
              </div>
              <div className='Dropdown-itemColumn'>
                <BedroomsBaths
                  name='baths'
                  options={uiStateStore.bedsBathsOptions}
                  onChange={onRadioButtonChange('baths')}
                  value={valueStateStore.values.baths}
                  legend='Bathrooms'
                />
              </div>
            </div>
            <SearchBarFooterActions
              cancelHandler={cancelBedsBathsDropdown}
              clearHandler={() => {
                valueStateStore.resetInputs(['beds', 'baths'])
                valueStateStore.getSearchInfoDebounced()
              }}
              applyResultsHandler={handleApplyBedsBaths}
              countResults={valueStateStore.countResults}
            />
          </Dropdown>
          </div>
          }
        </div>

        <div className={`SearchBar-mobileAdvancedFilter ${isAdvancedFilterActive ? 'is-active' : ''}`}>
          <AdvancedFilter
            submit={handleAdvancedFilterChange}
            reset={() => valueStateStore.handleResetFilters()}
            onActiveChanged={setIsAdvancedFilterActive}
            registerDropdown={registerDropdown}
            cancelDropdown={cancelAdvancedFilterDropdown}
          />
        </div>
      </div>
    </div>
  )
}

SearchBarMobile.propTypes = {
  listingTypeStore: PropTypes.object.isRequired,
  onChangePrice: PropTypes.func.isRequired,
  onChangePricePeriod: PropTypes.func.isRequired,
  onChangeTown: PropTypes.func.isRequired,
  onChangeYear: PropTypes.func.isRequired,
  onImmediateSubmit: PropTypes.func.isRequired,
  onRadioButtonChange: PropTypes.func.isRequired,
  openMapView: PropTypes.func.isRequired,
  uiStateStore: PropTypes.object.isRequired,
  valueStateStore: PropTypes.object.isRequired,
  registerDropdown: PropTypes.func.isRequired,
  cancelPriceDropdown: PropTypes.func.isRequired,
  cancelPricePeriodDropdown: PropTypes.func.isRequired,
  cancelBedsBathsDropdown: PropTypes.func.isRequired,
  cancelAdvancedFilterDropdown: PropTypes.func.isRequired
}

export default enhance(SearchBarMobile)
